import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Lock, Award } from "lucide-react";
import { ConfilexMark, ConfilexWordmark, BRAND_TAGLINE } from "@/components/ConfilexLogo";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isEn = (i18n.resolvedLanguage || "fr").startsWith("en");

  const PLATFORM_LINKS = isEn ? [
    { to: "/dossier", label: "Find an spécialiste" },
    { to: "/verifier", label: "Document checker" },
    { to: "/insights", label: "Insights & analyses" },
    { to: "/pricing", label: "Pricing" },
    { to: "/expert/apply", label: "Become a vetted spécialiste" },
  ] : [
    { to: "/dossier", label: "Trouver mon spécialiste" },
    { to: "/verifier", label: "Vérificateur de documents" },
    { to: "/insights", label: "Insights & analyses" },
    { to: "/pricing", label: "Tarifs" },
    { to: "/expert/apply", label: "Devenir spécialiste référencé" },
  ];

  const ABOUT_LINKS = isEn ? [
    { to: "/about", label: "Our story" },
    { to: "/galerie", label: "Gallery · Sorola" },
    { to: "/security", label: "Security & ethics" },
    { to: "/legal", label: "Legal · Terms · Privacy" },
    { to: "/code-conformite", label: "Compliance Code" },
  ] : [
    { to: "/about", label: "Notre histoire" },
    { to: "/galerie", label: "Galerie · Sorola" },
    { to: "/security", label: "Sécurité & déontologie" },
    { to: "/legal", label: "Mentions légales · CGU · Confidentialité" },
    { to: "/code-conformite", label: "Code de Conformité" },
  ];

  const introText = isEn
    ? "CONFILEX is a professional matchmaking platform. It facilitates access to a curated network of spécialistes in law, taxation, accounting, wealth and corporate strategy, in a secure and confidential environment."
    : "CONFILEX est une plateforme professionnelle de mise en relation. Elle facilite l'accès à un réseau sélectif de spécialistes du droit, de la fiscalité, de la comptabilité, du patrimoine et de la stratégie d'entreprise, dans un environnement sécurisé et confidentiel.";

  const ethics = isEn
    ? "CONFILEX is not a law firm, accountancy practice, notary or tax advisory firm. No analysis engaging professional liability is produced by the platform. Content generated by our AI tools is strictly informational and documentary; it must be verified by a qualified professional before any decision. The selected spécialiste retains full control over their analysis and recommendations."
    : "CONFILEX n'est pas un cabinet d'avocats, d'experts-comptables, de notaires ni de conseil fiscal. Aucune analyse engageant une responsabilité professionnelle n'est produite par la plateforme. Les contenus générés automatiquement par nos outils IA ont une vocation strictement informative et documentaire ; ils doivent être vérifiés par un professionnel compétent avant toute décision. Le spécialiste sollicité conserve l'entière maîtrise de son analyse et de ses recommandations.";

  const platformLabel = isEn ? "PLATFORM" : "PLATEFORME";
  const aboutLabel = isEn ? "ABOUT" : "À PROPOS";
  const ethicsLabel = isEn ? "Ethics notice · " : "Mention déontologique · ";
  const copyright = isEn
    ? `© ${new Date().getFullYear()} CONFILEX. ${t("footer.allRightsReserved")}.`
    : `© ${new Date().getFullYear()} CONFILEX. Tous droits réservés.`;
  const footerNote = isEn
    ? "CONFILEX is a professional matchmaking platform. It does not engage in any legal, tax or accounting advisory activity."
    : "CONFILEX est une plateforme professionnelle de mise en relation. Elle n'exerce aucune activité de conseil juridique, fiscal ou comptable.";

  return (
    <footer className="bg-[#0B3A2A] text-white/70 mt-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-2">
            <ConfilexMark size={48} theme="dark" />
            <ConfilexWordmark size={26} theme="dark" />
          </div>
          <p className="text-xs italic text-[#C9A961]/90 mb-4 ml-[60px]">{BRAND_TAGLINE}</p>
          <p className="text-sm leading-relaxed max-w-md">{introText}</p>
          <div className="flex items-center gap-4 mt-6 text-xs flex-wrap">
            <Link to="/security" className="flex items-center gap-1.5 hover:text-[#C9A961]" data-testid="footer-security-link">
              <ShieldCheck className="w-4 h-4 text-[#C9A961]" /> {isEn ? "Security & confidentiality" : "Sécurité & confidentialité"}
            </Link>
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#C9A961]" /> {isEn ? "GDPR Art. 32" : "RGPD Art. 32"}</span>
            <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#C9A961]" /> {isEn ? "EU hosting" : "Hébergement UE"}</span>
          </div>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-social" />
        </div>
        <div>
          <h4 className="text-white font-medium mb-4 text-sm uppercase tracking-wider">{platformLabel}</h4>
          <ul className="space-y-2 text-sm">
            {PLATFORM_LINKS.map(l => (
              <li key={l.to}><Link to={l.to} className="hover:text-[#C9A961]">{l.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4 text-sm uppercase tracking-wider">{aboutLabel}</h4>
          <ul className="space-y-2 text-sm">
            {ABOUT_LINKS.map(l => (
              <li key={l.to}><Link to={l.to} className="hover:text-[#C9A961]">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 text-[11px] text-white/55 leading-relaxed" data-testid="deontology-banner">
          <p>
            <span className="text-[#C9A961] font-medium uppercase tracking-wider text-[10px]">{ethicsLabel}</span>
            {ethics}
          </p>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs flex flex-col md:flex-row justify-between gap-2">
          <span>{copyright}</span>
          <span className="italic text-white/50">{footerNote}</span>
        </div>
      </div>
    </footer>
  );
}
